import { useState, useEffect, useMemo } from 'react';
import { EPISODES_DATA, type Episode } from '../data/episodes';
import { SectionLabel } from './Effects';
import { ArrowIcon } from './Icons';

export function EpisodesSection() {
  const [episodes, setEpisodes] = useState<Episode[]>(EPISODES_DATA);
  const [selectedSeason, setSelectedSeason] = useState<number | 'all'>('all');
  const [selectedCreator, setSelectedCreator] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'oldest' | 'newest'>('oldest');
  const [activeVideo, setActiveVideo] = useState<Episode | null>(null);

  // Fetch real-time updates without ever dropping our curated episodes
  useEffect(() => {
    fetch('/api/episodes')
      .then((res) => res.json())
      .then((data) => {
        if (data?.episodes && Array.isArray(data.episodes) && data.episodes.length > 0) {
          // Merge while preserving all EPISODES_DATA
          const map = new Map<string, Episode>();
          EPISODES_DATA.forEach((ep) => map.set(ep.id, ep));
          data.episodes.forEach((ep: Episode) => map.set(ep.id, ep));
          setEpisodes(Array.from(map.values()));
        }
      })
      .catch(() => {});
  }, []);

  // Extract unique creators
  const creators = useMemo(() => {
    const set = new Set<string>();
    episodes.forEach((ep) => set.add(ep.creator));
    return Array.from(set);
  }, [episodes]);

  // Filter and sort
  const filteredEpisodes = useMemo(() => {
    let list = [...episodes];

    if (selectedSeason !== 'all') {
      list = list.filter((ep) => ep.season === selectedSeason);
    }

    if (selectedCreator !== 'all') {
      list = list.filter((ep) => ep.creator === selectedCreator);
    }

    if (sortOrder === 'newest') {
      list.reverse();
    }

    return list;
  }, [episodes, selectedSeason, selectedCreator, sortOrder]);

  return (
    <section id="episodes" className="episodes-section section-pad">
      <SectionLabel index="04">VIDEO ARCHIVE & BROADCASTS</SectionLabel>

      <div className="split-heading" data-reveal>
        <div>
          <h2>
            Uge Appage SMP<br />
            <em>The Complete Chronicle.</em>
          </h2>
          <p>
            The entire saga of Uge Appage SMP—from the classic Reborn origins and founding of No Port City,
            to the live warfare, sovereign empires, and newest Season 3 expeditions. Watch directly from our sovereign portal.
          </p>
        </div>
        <div className="episodes-stat-pill">
          <b>{episodes.length}</b>
          <small>AUTO-SYNCED BROADCASTS</small>
        </div>
      </div>

      {/* Embedded Cinema Player Modal */}
      {activeVideo && (
        <div
          className="cinema-modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => setActiveVideo(null)}
        >
          <div className="cinema-modal-shell" onClick={(e) => e.stopPropagation()}>
            <div className="cinema-header">
              <div className="cinema-title-wrap">
                <span className="live-chip">{activeVideo.tag}</span>
                <h3>{activeVideo.title}</h3>
                <small>{activeVideo.creator} · {activeVideo.published || 'Archived'}</small>
              </div>
              <button
                className="cinema-close-btn"
                onClick={() => setActiveVideo(null)}
                aria-label="Close Cinema Player"
              >
                ✕
              </button>
            </div>
            <div className="cinema-embed-wrap">
              <iframe
                src={`https://www.youtube.com/embed/${activeVideo.id}?autoplay=1&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
                title={activeVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
            <div className="cinema-footer">
              <a
                href={activeVideo.url}
                target="_blank"
                rel="noreferrer"
                className="outline-link cinema-yt-link"
              >
                Watch on YouTube <ArrowIcon />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Filters Toolbar */}
      <div className="episodes-toolbar" data-reveal>
        <div className="toolbar-group">
          <label>Season:</label>
          <div className="filter-chips">
            <button
              className={`filter-chip ${selectedSeason === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedSeason('all')}
            >
              All Seasons
            </button>
            <button
              className={`filter-chip ${selectedSeason === 3 ? 'active' : ''}`}
              onClick={() => setSelectedSeason(3)}
            >
              Season 3
            </button>
            <button
              className={`filter-chip ${selectedSeason === 1 ? 'active' : ''}`}
              onClick={() => setSelectedSeason(1)}
            >
              Classic / Reborn
            </button>
          </div>
        </div>

        <div className="toolbar-group">
          <label>Creator:</label>
          <div className="filter-chips">
            <button
              className={`filter-chip ${selectedCreator === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCreator('all')}
            >
              All Creators
            </button>
            {creators.map((c) => (
              <button
                key={c}
                className={`filter-chip ${selectedCreator === c ? 'active' : ''}`}
                onClick={() => setSelectedCreator(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="toolbar-group sort-group">
          <label>Order:</label>
          <div className="filter-chips">
            <button
              className={`filter-chip ${sortOrder === 'oldest' ? 'active' : ''}`}
              onClick={() => setSortOrder('oldest')}
              title="Oldest classic episodes to latest"
            >
              Oldest First
            </button>
            <button
              className={`filter-chip ${sortOrder === 'newest' ? 'active' : ''}`}
              onClick={() => setSortOrder('newest')}
              title="Latest releases first"
            >
              Newest First
            </button>
          </div>
        </div>
      </div>

      {/* Episodes Grid */}
      <div className="episodes-grid" data-reveal>
        {filteredEpisodes.map((ep, idx) => (
          <article
            key={ep.id}
            className="episode-card"
            onClick={() => setActiveVideo(ep)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setActiveVideo(ep);
            }}
          >
            <div className="episode-thumbnail-wrap">
              <img
                src={ep.thumbnail}
                alt={ep.title}
                loading="lazy"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = `https://i.ytimg.com/vi/${ep.id}/mqdefault.jpg`;
                }}
              />
              <div className="episode-overlay-play">
                <span className="play-triangle">▶</span>
              </div>
              <span className={`episode-tag-badge ${ep.isLive ? 'live' : ''}`}>
                {ep.isLive && <i className="live-dot" />}
                {ep.tag}
              </span>
              {ep.length && <span className="episode-duration">{ep.length}</span>}
              <span className="episode-index-badge">#{idx + 1}</span>
            </div>

            <div className="episode-info">
              <div className="episode-meta">
                <span className="episode-creator">{ep.creator}</span>
                <span className="episode-dot">·</span>
                <span className="episode-pub">{ep.published || `Season ${ep.season}`}</span>
              </div>
              <h3 className="episode-title" title={ep.title}>
                {ep.title}
              </h3>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
